import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { QrCode } from '@app/modules/model/qrcode';

import { languageSupport, languageService } from '@app/modules/common/language'
import { syncService } from '@app/modules/sync';
import { viewService } from '@app/modules/view';

@Component({
    selector: 'ui-table-thumbnail',
    templateUrl:'./ui-thumb.html',
    styleUrls: [ 
        './ui-thumb.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiTableThumbnail extends languageSupport implements OnInit, OnChanges, OnDestroy {
    @Input('table') _table: QrCode = null;
    @Input('target') _target = 'view';  // 'view' / 'select' / 'tblno'
    @Input('parent') _parent = null;    // 'extra' / 'area' / 'possvc' (in case target is select)
    @Input('checked') _checked = false;

    private _subscriptions = [];

    /***********************************/
    /* DATA COMPONENTS                 */
    /***********************************/

    get HasExtras(){
        for(let _extra of this._table.place.extras){
            if (_extra.IsValid){
                let _valid = _extra.tables.some(
                _extratable => {
                    return (_extratable.qrcode == this._table) && _extratable.IsValid;
                });

                if (_valid){
                    return true;
                }
            }
        }

        return false;
    }

    private _cart_subsription = null;

    private get _TicketEdition(){
        if (this._cart_subsription){
            this._cart_subsription.unsubscribe();
            this._cart_subsription = null;
        }

        for(let _cartticket of this._table.place.cart){
            if ((_cartticket.qrcode == this._table) && (_cartticket.status == 'PR') && (_cartticket.IsRecent)){
                this._cart_subsription = _cartticket.OnRefresh.subscribe(
                data => {
                    this.change.markForCheck();
                });

                return !_cartticket.IsEmpty;
            }
        }

        return false;
    }

    get Status(){
        if (this._table.status == 'PA'){
            return 'wait';
        }

        if (this._table.IsError){
            return 'cnfg';
        }

        if (this._table.Attention){
            return 'goto';
        }

        if (this._table.IsBusy){
            return 'busy';
        }

        if (this._TicketEdition){
            return 'work';
        }

        return 'free';
    }

    private _toColor(color){
        if (this.view.LightTheme){
            if (color == 'medium'){
                return 'dark';
            }
        }

        return color;
    }

    get CardColor(){
        if (this._target == 'select'){
            return (this._checked) ? this._toColor('success') : this._toColor('medium');
        }
        else {
            switch(this.Status){
                case 'free': return this._toColor('medium');
                case 'cnfg': return this._toColor('danger');
                case 'work': return this._toColor('medium');
                case 'busy': return this._toColor('success');
                case 'goto': return this._toColor('warning');
            }
            return this._toColor('medium');    
        }
    }

    get ItemColor(){
        if (this.CardColor == this._toColor('medium')){
            return this._toColor('light');
        }
        else {
            if (this.view.LightTheme){
                return this._toColor('dark');
            }
            else {
                return this._toColor('light');
            }
        }
    }

    get LabelColor(){
        let _color = this.CardColor;
        
        if ((_color == this._toColor('success')) && (this.view.LightTheme)){
            return this._toColor('light');
        }

        return _color;
    }

    get Icon(){
        if (this._target == 'select'){
            return 'scan-circle';
        }
        else {
            switch(this.Status){
                case 'free': return 'scan-circle';
                case 'cnfg': return 'settings-outline';
                case 'work': return 'pause-circle-outline';
                case 'busy': return 'people-circle';
                case 'goto': return 'hand-right-outline';
            }

            return 'scan-circle';    
        }
    }

    get Label(){
        if (this._target == 'select'){
            switch(this._parent){
                case 'extra':
                    return this.lang.tr('@place_table_status_extras', [ this._table.extras.length ]);
                case 'area':
                    return (this._table.area?.IsValid) ? this._table.area.name || this.lang.tr('@place_area_noname') : this.lang.tr('@place_table_status_noarea');
                case 'till':
                    return (this._table.till?.IsValid) ? this._table.till.name || this.lang.tr('@place_till_noname') : this.lang.tr('@place_table_status_notill');
            }
        }

        return this.lang.tr('@place_table_status_' + this.Status);
    }

    get Number(){
        return this._table.number;
    }

    // Etiqueta visible de la mesa: alias si lo tiene; si no, "prefijo: número" (o
    // solo el número si no hay prefijo). Unifica lo que antes hacían dos <span>.
    get DisplayName(){
        if (this._table.alias){
            return this._table.alias;
        }
        if (this._table.prefix){
            return this._table.prefix + ': ' + this._table.number;
        }
        // Sin alias ni prefijo: "Mesa: N" (igual que producción, no solo el número).
        return this.lang.tr('@place_table_number', [ this._table.number ]);
    }

    /***********************************/
    /* STARTS HERE                     */
    /***********************************/

    constructor(private lang: languageService, private change: ChangeDetectorRef, private sync: syncService, public view: viewService) {
        super(lang, change);
    }

    ngOnInit(){
        // refresh on table changes
        if (this._table){
            this._subscriptions.push(   
                this._table.OnRefresh.subscribe(
                data => {
                    this.change.markForCheck();
                })
            );    
        }

        // refresh on theme changed
        this._subscriptions.push(
            this.view.OnTheme.subscribe(
            data => {
                this.change.markForCheck();
            })
        );

        // refresh every 10 seconds (for online check)
        this._subscriptions.push(    
            this.sync.Clock.OnTenSecondTick.subscribe(
            data => {
                this.change.markForCheck();
            })
        );
    }

    private _table_subscription = null;
    private _place_subscription = null;

    ngOnChanges(){
        if (this._table_subscription){
            this._table_subscription.unsubscribe();
            this._table_subscription = null;
        }

        if (this._place_subscription){
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        if (this._table){
            // refresh on table changes
            this._table_subscription = this._table.OnRefresh.subscribe(
            data => {
                this.change.markForCheck();
            });
              
            // refresh on cart changes
            this._place_subscription = this._table.place.OnCart.subscribe(
            data => {
                this.change.markForCheck();
            });           
        }
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._table_subscription){
            this._table_subscription.unsubscribe();
            this._table_subscription = null;
        }

        if (this._place_subscription){
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        if (this._cart_subsription){
            this._cart_subsription.unsubscribe();
            this._cart_subsription = null;
        }

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];
    }

    @Output('onClick') _onClick = new EventEmitter<any>();         
    async OnSelect(){
        this._onClick.emit(this._table);
    }
}
